import { FC, useCallback, useContext } from 'react'
import { useSelector } from 'react-redux'

import { ScreenContext } from 'react-components'
import { useTranslation } from '../../../../hooks/locale'
import ObjectOverviewCard from '../../../common/object-overview-card'
import GridCard from '../../../../components/common/grid-card'
import { ListingSectionObject } from '../../../../transports/common/listing-section-transport'
import { cardEventCategories, pageTypeEventCategories } from '../../../../services/analytics-service/categories'
import { Offer } from '../../../../models/common/offers-model'
import { SuitablesModel } from '../../../../models/common/suitables-model'
import { BreadcrumbListTransport } from '../../../../models/common/breadcrumb-model'
import { isBulkInquiryEligible } from '../../../../utils/bulk-inquiry-utils'
import {
    getIsListingSelected,
    getIsSelectionFull,
    selectListing,
    deselectListing,
} from '../../../../redux/bulk-inquiry'
import { useAppDispatch } from '../../../../redux/hooks'
import { useBulkInquiry } from '../bulk-inquiry/use-bulk-inquiry'
import AnalyticsService from '../../../../services/analytics-service'
import { bulkInquiryPageCategories } from '../../../../services/analytics-service/categories'
import { bulkInquiryPageActions } from '../../../../services/analytics-service/actions'

interface Props {
    listing: ListingSectionObject
    index: number
    isGridView?: boolean
    breadcrumbModel: BreadcrumbListTransport
    offerModel: Offer
    suitablesModel: SuitablesModel
    isAboveFold?: boolean
    propagateSearchRequestId?: boolean
}

const ListingCard: FC<Props> = ({
    listing,
    index,
    isGridView = false,
    breadcrumbModel,
    offerModel,
    suitablesModel,
    isAboveFold,
    propagateSearchRequestId = false,
}) => {
    const { isMobile } = useContext(ScreenContext)
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const { isEnabled, isSelectMode } = useBulkInquiry()
    const isSelected = useSelector(getIsListingSelected(listing.id))
    const isSelectionFull = useSelector(getIsSelectionFull)

    const isIneligible = !isBulkInquiryEligible(listing)
    // Same checkbox, same position, for every card — ineligible listings just render it disabled
    // rather than not rendering it at all.
    const showCheckbox = isEnabled && isSelectMode
    const isCheckboxDisabled = isIneligible || (isSelectionFull && !isSelected)
    const disabledTooltip = isIneligible ? t('bulkInquiry.ineligibleTooltip') : undefined

    const handleToggleSelect = useCallback(
        (id: number) => {
            if (isSelected) {
                AnalyticsService.trackEvent({
                    category: bulkInquiryPageCategories.SRL,
                    action: bulkInquiryPageActions.LISTING_DESELECTED_FROM_CARD,
                    value: String(id),
                })
                dispatch(deselectListing(String(id)))
            } else {
                AnalyticsService.trackEvent({
                    category: bulkInquiryPageCategories.SRL,
                    action: bulkInquiryPageActions.LISTING_SELECTED,
                    value: String(id),
                })
                dispatch(selectListing(listing))
            }
        },
        [isSelected, listing, dispatch],
    )

    const bulkSelectProps = {
        showCheckbox,
        isSelected,
        isCheckboxDisabled,
        disabledTooltip,
        checkboxVariant: 'inline' as const,
        onToggleSelect: handleToggleSelect,
    }

    if (isMobile || isGridView) {
        return (
            <GridCard
                index={index}
                listingObject={listing}
                displayPrice
                displayPriceInfo
                requestUrl=""
                isObjectOverview={false}
                showCtaButton
                pageName={pageTypeEventCategories.SEARCH_RESULT_LISTING_PAGE}
                sectionName={cardEventCategories.sections.LISTING_CARD}
                useCarousel
                breadcrumbModel={breadcrumbModel}
                offerModel={offerModel}
                suitablesModel={suitablesModel}
                showFeatures
                showFeatureLabel={false}
                isAboveFold={isAboveFold}
                propagateSearchRequestId={propagateSearchRequestId}
                {...bulkSelectProps}
            />
        )
    }

    return (
        <ObjectOverviewCard
            index={index}
            pageName={pageTypeEventCategories.SEARCH_RESULT_LISTING_PAGE}
            sectionName={cardEventCategories.sections.LISTING_CARD}
            useCarousel
            listingObject={listing}
            showCtaButton
            showBreadcrumbsSection={false}
            breadcrumbModel={breadcrumbModel}
            offerModel={offerModel}
            suitablesModel={suitablesModel}
            isAboveFold={isAboveFold}
            propagateSearchRequestId={propagateSearchRequestId}
            {...bulkSelectProps}
        />
    )
}

export default ListingCard
